import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  Modal,
  Select,
  Switch,
  Text,
  Textarea,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import { knowledgeBaseService } from "services/knowledgeBaseService";
import {
  CreateWebSourceRequest,
  KbWebSourceDto,
  UpdateWebSourceRequest,
} from "types/knowledgeBase";

interface WebSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingSource?: KbWebSourceDto | null;
  defaultFolderId?: string;
}

const WebSourceModal = ({
  isOpen,
  onClose,
  onSuccess,
  editingSource,
  defaultFolderId,
}: WebSourceModalProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [seedUrls, setSeedUrls] = useState("");
  const [maxPages, setMaxPages] = useState(1);
  const [rateLimit, setRateLimit] = useState(20);
  const [scope, setScope] = useState<"HOST_ONLY" | "SUBDOMAINS">("HOST_ONLY");
  const [inclusionFilters, setInclusionFilters] = useState("");
  const [exclusionFilters, setExclusionFilters] = useState("");
  const [isGlobal, setIsGlobal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!editingSource;

  // Reset form when modal opens/closes or editing source changes
  useEffect(() => {
    if (isOpen) {
      if (editingSource) {
        setName(editingSource.name);
        setSeedUrls(editingSource.seedUrls.join("\n"));
        setMaxPages(editingSource.maxPages);
        setRateLimit(editingSource.rateLimit);
        setScope(editingSource.scope);
        setInclusionFilters(editingSource.inclusionFilters?.join("\n") || "");
        setExclusionFilters(editingSource.exclusionFilters?.join("\n") || "");
        setIsGlobal(editingSource.isGlobal);
      } else {
        setName("");
        setSeedUrls("");
        setMaxPages(1);
        setRateLimit(20);
        setScope("HOST_ONLY");
        setInclusionFilters("");
        setExclusionFilters("");
        setIsGlobal(false);
      }
      setError(null);
    }
  }, [isOpen, editingSource]);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const parseUrls = (text: string): string[] => {
    return text
      .split("\n")
      .map((url) => url.trim())
      .filter((url) => url.length > 0);
  };

  const parseFilters = (text: string): string[] | undefined => {
    const filters = text
      .split("\n")
      .map((f) => f.trim())
      .filter((f) => f.length > 0);
    return filters.length > 0 ? filters : undefined;
  };

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validate = (): string | null => {
    if (!name.trim()) {
      return t("app.knowledgeBase.webSources.validation.nameRequired");
    }

    const urls = parseUrls(seedUrls);
    if (urls.length === 0) {
      return t("app.knowledgeBase.webSources.validation.urlsRequired");
    }

    for (const url of urls) {
      if (!validateUrl(url)) {
        return `${t("app.knowledgeBase.webSources.validation.invalidUrl")}: ${url}`;
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      if (isEditing && editingSource) {
        const request: UpdateWebSourceRequest = {
          name: name.trim(),
          seedUrls: parseUrls(seedUrls),
          maxPages,
          rateLimit,
          scope,
          inclusionFilters: parseFilters(inclusionFilters),
          exclusionFilters: parseFilters(exclusionFilters),
          isGlobal,
        };
        await knowledgeBaseService.updateWebSource(editingSource.id, request);
      } else {
        const request: CreateWebSourceRequest = {
          name: name.trim(),
          seedUrls: parseUrls(seedUrls),
          maxPages,
          rateLimit,
          scope,
          inclusionFilters: parseFilters(inclusionFilters),
          exclusionFilters: parseFilters(exclusionFilters),
          isGlobal,
          folderId: defaultFolderId,
        };
        await knowledgeBaseService.createWebSource(request);
      }

      handleClose();
      onSuccess();
    } catch (err: any) {
      const message = isEditing
        ? t("app.knowledgeBase.webSources.update.failed")
        : t("app.knowledgeBase.webSources.create.failed");
      setError(err.response?.data?.message || message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>
        <Flex justify="space-between" align="center">
          <Heading size="lg" color={{ base: "navy.700", _dark: "white" }}>
            {isEditing
              ? t("app.knowledgeBase.webSources.editWebSource")
              : t("app.knowledgeBase.webSources.addWebSource")}
          </Heading>
          <Modal.CloseTrigger asChild>
            <Button variant="ghost" size="sm" p={0}>
              <MdClose className="h-6 w-6" />
            </Button>
          </Modal.CloseTrigger>
        </Flex>
      </Modal.Header>

      <Modal.Body>
        <VStack gap={4}>
          {/* Error message */}
          {error && (
            <Flex
              w="full"
              p={3}
              borderRadius="lg"
              bg={{ base: "red.100", _dark: "red.900" }}
              color={{ base: "red.700", _dark: "red.100" }}
            >
              <Text fontSize="sm">{error}</Text>
            </Flex>
          )}

          {/* Name */}
          <Box w="full">
            <Text
              mb={1}
              fontWeight="medium"
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.knowledgeBase.webSources.name")} *
            </Text>
            <Input
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              placeholder={t("app.knowledgeBase.webSources.namePlaceholder")}
            />
          </Box>

          {/* Seed URLs */}
          <Box w="full">
            <Text
              mb={1}
              fontWeight="medium"
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.knowledgeBase.webSources.seedUrls")} *
            </Text>
            <Textarea
              value={seedUrls}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setSeedUrls(e.target.value)
              }
              placeholder={t(
                "app.knowledgeBase.webSources.seedUrlsPlaceholder",
              )}
              rows={4}
            />
            <Text
              fontSize="xs"
              color={{ base: "gray.500", _dark: "gray.400" }}
              mt={1}
            >
              {t("app.knowledgeBase.webSources.seedUrlsHint")}
            </Text>
          </Box>

          {/* Max Pages and Rate Limit */}
          <HStack w="full" gap={4}>
            <Box flex={1}>
              <Text
                mb={1}
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.knowledgeBase.webSources.maxPages")}
              </Text>
              <Input
                type="number"
                value={maxPages}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setMaxPages(parseInt(e.target.value) || 1)
                }
                min={1}
                max={500}
              />
              <Text
                fontSize="xs"
                color={{ base: "gray.500", _dark: "gray.400" }}
                mt={1}
              >
                {t("app.knowledgeBase.webSources.maxPagesHint")}
              </Text>
            </Box>
            <Box flex={1}>
              <Text
                mb={1}
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.knowledgeBase.webSources.rateLimit")}
              </Text>
              <Input
                type="number"
                value={rateLimit}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRateLimit(parseInt(e.target.value) || 20)
                }
                min={1}
                max={300}
              />
              <Text
                fontSize="xs"
                color={{ base: "gray.500", _dark: "gray.400" }}
                mt={1}
              >
                {t("app.knowledgeBase.webSources.rateLimitHint")}
              </Text>
            </Box>
          </HStack>

          {/* Scope */}
          <Box w="full">
            <Text
              mb={1}
              fontWeight="medium"
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.knowledgeBase.webSources.scope")}
            </Text>
            <Select
              items={[
                {
                  label: t("app.knowledgeBase.webSources.scopeHostOnly"),
                  value: "HOST_ONLY",
                },
                {
                  label: t("app.knowledgeBase.webSources.scopeSubdomains"),
                  value: "SUBDOMAINS",
                },
              ]}
              value={[scope]}
              onValueChange={(details) =>
                setScope(details.value[0] as "HOST_ONLY" | "SUBDOMAINS")
              }
            />
          </Box>

          {/* Inclusion Filters */}
          <Box w="full">
            <Text
              mb={1}
              fontWeight="medium"
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.knowledgeBase.webSources.inclusionFilters")}
            </Text>
            <Textarea
              value={inclusionFilters}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setInclusionFilters(e.target.value)
              }
              placeholder={t(
                "app.knowledgeBase.webSources.inclusionFiltersPlaceholder",
              )}
              rows={2}
            />
            <Text
              fontSize="xs"
              color={{ base: "gray.500", _dark: "gray.400" }}
              mt={1}
            >
              {t("app.knowledgeBase.webSources.inclusionFiltersHint")}
            </Text>
          </Box>

          {/* Exclusion Filters */}
          <Box w="full">
            <Text
              mb={1}
              fontWeight="medium"
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.knowledgeBase.webSources.exclusionFilters")}
            </Text>
            <Textarea
              value={exclusionFilters}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setExclusionFilters(e.target.value)
              }
              placeholder={t(
                "app.knowledgeBase.webSources.exclusionFiltersPlaceholder",
              )}
              rows={2}
            />
            <Text
              fontSize="xs"
              color={{ base: "gray.500", _dark: "gray.400" }}
              mt={1}
            >
              {t("app.knowledgeBase.webSources.exclusionFiltersHint")}
            </Text>
          </Box>

          {/* Global KB toggle */}
          <Box
            w="full"
            p={3}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={{ base: "gray.200", _dark: "gray.600" }}
          >
            <HStack justify="space-between">
              <Box>
                <Text
                  fontWeight="medium"
                  color={{ base: "navy.700", _dark: "white" }}
                >
                  Global KB
                </Text>
                <Text
                  fontSize="xs"
                  color={{ base: "gray.500", _dark: "gray.400" }}
                >
                  When enabled, this web source is automatically included in all
                  widgets and cannot be removed from a widget's KB list.
                </Text>
              </Box>
              <Switch
                checked={isGlobal}
                onCheckedChange={(details: any) => setIsGlobal(details.checked)}
                colorPalette="blue"
              />
            </HStack>
          </Box>
        </VStack>
      </Modal.Body>

      <Modal.Footer>
        <HStack gap={3} w="full">
          <Button
            variant="subtle"
            colorPalette="gray"
            onClick={handleClose}
            disabled={isSubmitting}
            flex={1}
          >
            {t("app.common.cancel")}
          </Button>
          <Button
            colorPalette="blue"
            onClick={handleSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
            flex={1}
          >
            {isEditing ? t("app.common.update") : t("app.common.create")}
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};

export default WebSourceModal;
